import {
  AnalyticsEnhancerSources,
  withEventTrackEnhancers,
  withPageTrackEnhancers,
} from '@analytics/enhancers';
import { createGDDLEvent, PageMetadataService } from '@analytics/utils';
import type { Datalayer } from '@analytics/models';
import { inject, Injectable } from '@angular/core';
import { waitForData } from '@innogy/utils-rxjs';
import { map, merge, of, zip } from 'rxjs';

import { DatalayerEventStream } from './event-stream.service';

/**
 * Service responsible for applying enhancers to events that are pushed to the datalayer.
 * After enhancers are applied the events are compliant with the GDDL specification.
 */
@Injectable({
  providedIn: 'root',
})
export class DatalayerEventStreamEnhancer {
  // Injecting required dependencies for analytics processing and metadata retrieval.
  readonly #enhancerSources = inject(AnalyticsEnhancerSources, {
    optional: true,
  });
  readonly #pageMetadata = inject(PageMetadataService);
  readonly #eventStream = inject(DatalayerEventStream);

  /**
   * Enhances analytics events with additional metadata and transformations.
   * Combines page and event tracking streams to produce enriched events.
   *
   * By default the added metadata contains:
   * - page url the event is triggered from, e.g. `https://www.essent.nl/energie`.
   * - page path the event is triggered from, e.g. `/energie`.
   * - and title the event is triggered from, e.g. `Energie van vandaag: stroom en gas | Essent`.
   * - referrer the current page is coming from, e.g. `https://www.google.com/q=goedkope-stroom`.
   * - domain the event is triggered from, e.g. `www.essent.nl`.
   * - user Agent data of device the event is triggered from.
   *
   * Extra metadata can be added to events by registering an event enhancer and providing it to the
   * core analytics module.
   *
   * @returns analytics event stream in GDDL format.
   */
  eventStreamAsGDDLEvents(datalayer: Datalayer) {
    // In case of no enhancers being provided, return an empty observable.
    if (!this.#enhancerSources) {
      return of();
    }

    // Enhancing page metadata with predefined page tracking enhancers.
    const enhancedPageInfo$ = this.#pageMetadata.pageInfo$.pipe(
      withPageTrackEnhancers(this.#enhancerSources, datalayer)
    );

    // Combining page track events with enhanced page metadata.
    const pageTrackEventsWithEnhancements$ = zip(
      this.#eventStream.pageTrack,
      enhancedPageInfo$
    ).pipe(map(([pageTrack, pageInfo]) => ({ pageTrack, pageInfo })));

    // Enhancing event track events and ensuring page metadata availability.
    const eventTrackEventsWithEnhancements$ = this.#eventStream.eventTrack.pipe(
      withEventTrackEnhancers(this.#enhancerSources, datalayer),
      waitForData(pageTrackEventsWithEnhancements$) // Ensures synchronization with page metadata.
    );

    // Merging enhanced page impression and event tracking streams into a unified stream of GDDL events.
    return merge(
      pageTrackEventsWithEnhancements$.pipe(
        map(({ pageInfo }) =>
          createGDDLEvent('page-impression', {}, { pageInfo })
        )
      ),
      eventTrackEventsWithEnhancements$.pipe(
        map(([event, { pageInfo }]) =>
          createGDDLEvent(event.event, event.info, { pageInfo })
        )
      )
    );
  }
}
